import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Alert, Button, Badge, Card, Row, Col, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faCheckCircle, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import AwsChatbot from '../AwsChatbot';
import AndroidAppFlavours from '../shared/AndroidAppFlavours';
import AdPlatformAppCard from '../ads/AdPlatformAppCard';
import packageJson from '../../../package.json';

// PII-Sanitized Demo Telemetry for Cloud Dashboard (CD)
const sanitizedDemoMetrics = {
  profile: {
    anonymousId: 'ANON-DEMO-VISITOR-01',
    tier: 'CD Foundation (Standard)',
    cloudRegion: 'eu-central-1 (Sanitized)',
    lastSyncTime: '10 minutes ago'
  },
  devices: [
    { name: 'Oura Ring Gen 3 (Demo)', battery: '82%', status: 'Connected', lastSync: '10 mins ago' },
    { name: 'Huawei Band 7 (Demo)', battery: '95%', status: 'Connected', lastSync: '25 mins ago' },
    { name: 'Samsung Galaxy Watch (Demo)', battery: '60%', status: 'Standby', lastSync: '1 hour ago' }
  ],
  telemetry: [
    { metric: 'Daily Sleep Architecture', value: '87 / 100', detail: 'Deep: 1h 48m | REM: 2h 12m | Light: 4h 10m', score: 'Optimal' },
    { metric: 'Daily Activity Telemetry', value: '10,480 steps', detail: '540 active kcal | 48 active minutes', score: 'Goal Met' },
    { metric: 'Autonomic Readiness & HRV', value: '64 ms', detail: 'Resting HR: 56 bpm | Body Temp: +0.1°C', score: 'Prime' }
  ]
};

const CDPage = () => {
  const { authState } = useOktaAuth();
  const location = useLocation();

  // Determine if demo mode is requested via URL (?demo=true) or default for unauthenticated
  const searchParams = new URLSearchParams(location.search);
  const urlDemo = searchParams.get('demo') === 'true';

  const [isDemoActive, setIsDemoActive] = useState(urlDemo);

  const isAuthenticated = Boolean(authState?.isAuthenticated);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem('cd_resource_opened', 'true');
      } catch (e) {}
    }
  }, []);

  return (
    <Container className="ecosystem-page py-5 mt-4">
      <header className="border-bottom pb-3 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h1 className="font-weight-bold mb-1">Cloud Dashboard (<abbr title="Cloud Dashboard">CD</abbr>)</h1>
            <p className="text-muted mb-0">
              The open baseline synchronization and telemetry interface for the HealthDash ecosystem. (v{packageJson.version})
            </p>
          </div>
          <div>
            {isDemoActive ? (
              <Badge bg="success" className="p-2 fs-6">
                <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                PII-Sanitized Demo Mode
              </Badge>
            ) : (
              <Badge bg="secondary" className="p-2 fs-6">
                {isAuthenticated ? 'Live Session' : 'Standard Visitor View'}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Demo Mode Activation & Non-Authorized Visitor Section */}
      {!isAuthenticated && !isDemoActive && (
        <Alert variant="warning" className="shadow-sm border-0 mb-4 p-4 rounded-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="alert-heading font-weight-bold mb-1">
                <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-warning" />
                Non-Authorized Visitor Preview
              </h5>
              <p className="mb-0 small text-dark">
                You are viewing the generic Cloud Dashboard. To experience how baseline synchronization telemetry
                is displayed without signing in, load our <strong>PII-sanitized demo dataset</strong>.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="warning"
                className="fw-bold text-white shadow-sm"
                onClick={() => setIsDemoActive(true)}
              >
                <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                Load Demo Data (PII-Sanitized)
              </Button>
              <Link to="/login" className="btn btn-outline-dark fw-bold">
                Sign In
              </Link>
            </div>
          </div>
        </Alert>
      )}

      {/* Demo Mode Active Banner */}
      {isDemoActive && (
        <Alert variant="info" className="shadow-sm border-0 mb-4 p-3 rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="small">
            <strong>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-info" />
              PII-Sanitized Demo Active:
            </strong>{' '}
            Displaying simulated wearable telemetry. No real personal identifiable information is utilized or stored.
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-info" size="sm" onClick={() => setIsDemoActive(false)}>
              Exit Demo
            </Button>
            {!isAuthenticated && (
              <Link to="/login" className="btn btn-info btn-sm text-white fw-bold">
                Login for Real Data
              </Link>
            )}
          </div>
        </Alert>
      )}

      {/* Main Content Area */}
      {isDemoActive ? (
        <div className="demo-telemetry-container">
          <Row className="mb-4 g-3">
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0 rounded-3">
                <Card.Header className="bg-light fw-bold py-2">
                  Visitor Session Profile
                </Card.Header>
                <Card.Body className="small">
                  <p className="mb-1"><strong>Anonymous ID:</strong> <code>{sanitizedDemoMetrics.profile.anonymousId}</code></p>
                  <p className="mb-1"><strong>Ecosystem Tier:</strong> {sanitizedDemoMetrics.profile.tier}</p>
                  <p className="mb-1"><strong>Sanitized Region:</strong> {sanitizedDemoMetrics.profile.cloudRegion}</p>
                  <p className="mb-0 text-muted"><strong>Last Sync:</strong> {sanitizedDemoMetrics.profile.lastSyncTime}</p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={8}>
              <Card className="h-100 shadow-sm border-0 rounded-3">
                <Card.Header className="bg-light fw-bold py-2">
                  Connected Sync Sources (Sanitized Demo)
                </Card.Header>
                <Card.Body className="small p-2">
                  <Row className="g-2">
                    {sanitizedDemoMetrics.devices.map(device => (
                      <Col sm={4} key={device.name}>
                        <div className="p-2 border rounded bg-light text-center h-100">
                          <div className="fw-bold">{device.name}</div>
                          <Badge bg={device.status === 'Connected' ? 'success' : 'secondary'} className="my-1">
                            {device.status}
                          </Badge>
                          <div className="text-muted small">Battery: {device.battery}</div>
                          <div className="text-muted small">{device.lastSync}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <h5 className="font-weight-bold mb-3">Sanitized Wearable Telemetry</h5>
          <Row className="g-3 mb-4">
            {sanitizedDemoMetrics.telemetry.map(item => (
              <Col md={4} key={item.metric}>
                <Card className="h-100 shadow-sm border-0 rounded-3 text-center p-3">
                  <h6 className="text-muted mb-2">{item.metric}</h6>
                  <div className="display-6 fw-bold text-warning mb-1">{item.value}</div>
                  <Badge bg="info" className="mx-auto mb-2 text-white">{item.score}</Badge>
                  <div className="small text-muted">{item.detail}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <div className="content-block bg-light p-4 rounded-3 shadow-sm mb-4">
          <h4>Standard Cloud Dashboard Overview</h4>
          <p className="text-muted">
            The standard Cloud Dashboard (<abbr title="Cloud Dashboard">CD</abbr>) provides unencumbered
            monitoring of mobile health synchronization metrics. Non-authorized visitors can trigger demo data loading
            using the button above to test the UI experience without an Okta account.
          </p>
          {!isAuthenticated && (
            <Button
              variant="outline-warning"
              className="fw-bold"
              onClick={() => setIsDemoActive(true)}
            >
              Open CD with Demo Data
            </Button>
          )}
        </div>
      )}

      {/* Expandable Android App Editions to Preserve Screen Estate */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold small text-secondary">Android Companion Applications</span>
          <span className="text-muted small">Android App</span>
        </div>
        <AndroidAppFlavours defaultOpen={false} />
      </Card>

      {/* Google Ads Platform App Card for CD Page Visitors */}
      <div className="d-flex justify-content-center mb-4">
        <AdPlatformAppCard className="col-12 col-md-8 col-lg-6" />
      </div>

      <AwsChatbot />
    </Container>
  );
};

export default CDPage;